/**
 * Fiat Account Activation Flow
 * Orchestrates the bank account activation process using DialogFlow
 */
import { useCallback, useEffect, useMemo, useState, type Ref } from 'react';

import DialogFlow from '@components/ui/dialog-flow/dialog-flow';
import type { DialogFlowInstance } from '@components/ui/dialog-flow/types';
import { useDialogFlow } from '@components/ui/dialog-flow/useDialogFlow';
import { StepFlow, useStepFlow } from '@components/ui/step-flow';
import { useApiMutation, useApiQuery } from '@shared-runtime/api/queryHelpers';
import { fetchActivateCurrency } from '@shared-runtime/modules/fiat/activate-currency';
import type { ActivateCurrencyParams } from '@shared-runtime/modules/fiat/activate-currency/apiSchema';
import {
  ActivationStatus,
  type ActivateCurrencyData,
} from '@shared-runtime/modules/fiat/activate-currency/schema';
import { fetchUploadDocuments } from '@shared-runtime/modules/fiat/documents';
import { fetchGetCurrencies } from '@shared-runtime/modules/fiat/get-currencies';
import {
  CURRENCY_STATUS,
  POA_STATUS,
  type CurrencyStatus,
  type GetCurrenciesResponse,
} from '@shared-runtime/modules/fiat/get-currencies/schema';
import {
  useCountry,
  type CountryStateType,
} from '@shared-web/modules/user/hooks/useCountry';

import { useFiatAccounts } from '../../hooks/useFiatAccounts';
import { ActivationCompleted } from './ActivationCompleted';
import { ActivationInProgress } from './ActivationInProgress';
import { AddressForm } from './AddressForm';
import { AddressReview } from './AddressReview';
import { CaraForm, CaraTypeEnum } from './CaraForm';
import { InformationSubmitted } from './InformationSubmitted';
import { ProofOfAddressRequired } from './ProofOfAddressRequired';
import { ProofOfAddressUpload } from './ProofOfAddressUpload';
import { VerificationFailed } from './VerificationFailed';
import { VerificationFailedFirstTime } from './VerificationFailedFirstTime';

function buildActivateParamsWithEDD(
  currency: string,
  paymentNetwork: string,
  vendorId: string,
  caraData: any,
): ActivateCurrencyParams {
  return {
    currency,
    tncAccepted: true,
    payment_network: paymentNetwork,
    vendor_id: vendorId,
    edd_questions: [
      {
        question_id: 'expected_annual_transaction_volume',
        answers: [{ id: caraData.annualTransactionVolume }],
      },
      {
        question_id: 'annual_income',
        answers: [{ id: caraData.annualIncome }],
      },
      {
        question_id: 'occupation',
        answers: [{ value: caraData.occupation }],
      },
      {
        question_id: 'sources_of_wealth',
        answers: [{ id: caraData.sourceOfWealth }],
      },
      {
        question_id: 'source_of_wealth_country',
        answers: [{ value: caraData.sourceOfFundsCountry }],
      },
      {
        question_id: 'funds_transfer_country',
        answers: [{ value: caraData.transactionCountry }],
      },
      {
        question_id: 'citizenship_by_investment',
        answers: [{ id: caraData.citizenshipByInvestment }],
      },
    ],
  };
}

/**
 * Build activate currency params with POA data from AddressForm
 */
function buildActivateParamsWithPOA(
  currency: string,
  paymentNetwork: string,
  vendorId: string,
  addressData: any,
  documents: any[] = [],
): ActivateCurrencyParams {
  return {
    currency,
    tncAccepted: true,
    payment_network: paymentNetwork,
    vendor_id: vendorId,
    poa: {
      documents,
      address: {
        address_line_1: addressData.streetName || '',
        address_line_2: addressData.buildingName || '',
        city: addressData.city || '',
        state: addressData.state || '',
        postcode: addressData.zipCode || '',
        country: addressData.country || '',
        national_identifier: addressData.ssn || '',
      },
    },
  };
}

/**
 * Determines the next activation step based on API response data
 *
 * @param data - The API response data
 * @returns The next step in the activation flow
 */
function determineNextStep(data: ActivateCurrencyData): ActivationStep {
  if (
    data.status === ActivationStatus.Activated ||
    data.status === ActivationStatus.Completed
  ) {
    return ActivationStep.InformationSubmitted;
  }

  if (data.status === ActivationStatus.PendingReview) {
    const poaStatus = data.steps.poa.status;

    switch (poaStatus) {
      case POA_STATUS.REJECTED:
      case POA_STATUS.FAILED:
        return ActivationStep.VerificationFailed;

      case POA_STATUS.PENDING_RETRY_AUTO_VERIFICATION:
      case POA_STATUS.PENDING_SUBMIT_MANUAL_REVIEW_ADDRESS_PROOF:
      case POA_STATUS.PENDING_RETRY_MANUAL_REVIEW_ADDRESS_PROOF:
        return ActivationStep.ProofOfAddressRequired;

      case POA_STATUS.PENDING_AUTO_VERIFICATION:
      case POA_STATUS.PENDING_MANUAL_REVIEW_ADDRESS_PROOF:
      case POA_STATUS.APPROVED:
        return ActivationStep.InformationSubmitted;

      default:
        return ActivationStep.VerificationFailed;
    }
  }

  return ActivationStep.VerificationFailed;
}

function getActivationDefaultStep(
  countryState: CountryStateType,
  currencyInfo?: CurrencyStatus,
  currency?: string,
): ActivationStep {
  if (!currencyInfo) {
    return ActivationStep.AddressForm;
  }

  if (currencyInfo.isActivated) {
    return ActivationStep.ActivationInProgress;
  }

  const needEDD =
    currency === 'GBP' || (currency === 'EUR' && countryState.isEEA);

  switch (currencyInfo.status) {
    case CURRENCY_STATUS.INITIALIZED:
    case CURRENCY_STATUS.TNC_ACCEPTED:
    case CURRENCY_STATUS.EDD_ALL_PENDING:
      if (needEDD) {
        return ActivationStep.CaraForm;
      } else {
        return ActivationStep.AddressForm;
      }
    case CURRENCY_STATUS.EDD_Q1_PENDING:
      return ActivationStep.CaraForm;

    case CURRENCY_STATUS.POA_AUTO_INITIATED:
    case CURRENCY_STATUS.POA_AUTO_PENDING:
      if (currencyInfo.poa?.address?.address_line_1) {
        return ActivationStep.AddressReview;
      } else {
        return ActivationStep.AddressForm;
      }

    case CURRENCY_STATUS.POA_AUTO_RETRY:
      return ActivationStep.AddressReview;

    case CURRENCY_STATUS.POA_MANUAL_INITIATED:
    case CURRENCY_STATUS.POA_MANUAL_RETRY:
      return ActivationStep.ProofOfAddressUpload;

    case CURRENCY_STATUS.POA_MANUAL_PENDING:
      return ActivationStep.InformationSubmitted;

    case CURRENCY_STATUS.POA_FAILED:
    case CURRENCY_STATUS.POA_REJECTED:
      return ActivationStep.VerificationFailed;

    case CURRENCY_STATUS.POA_APPROVED:
    case CURRENCY_STATUS.FIAT_PENDING:
      return ActivationStep.ActivationInProgress;

    case CURRENCY_STATUS.FIAT_APPROVED:
      return ActivationStep.ActivationCompleted;

    case CURRENCY_STATUS.FIAT_FAILED:
      return ActivationStep.VerificationFailed;

    default:
      return ActivationStep.AddressForm;
  }
}

/**
 * Activation flow step identifiers
 */
export enum ActivationStep {
  CaraForm = 'cara-form',
  AddressForm = 'address-form',
  AddressReview = 'address-review',
  InformationSubmitted = 'information-submitted',
  ActivationInProgress = 'activation-in-progress',
  ActivationFailedFirstTime = 'activation-failed-first-time',
  ProofOfAddressRequired = 'proof-of-address-required',
  ProofOfAddressUpload = 'proof-of-address-upload',
  VerificationFailed = 'verification-failed',
  ActivationCompleted = 'activation-completed',
}

export type ActivationStepType = `${ActivationStep}`;

interface ActivationFlowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currency: string;
  paymentNetwork: string;
  vendorId: string;
  currencies?: GetCurrenciesResponse;
  isPageFlow?: boolean;
}

/**
 * Fiat Account Activation Flow Component
 *
 * Flow sequence:
 * 1. AddressForm - User enters SSN and residential address
 * 2. AddressReview - User reviews entered information
 * 3. InformationSubmitted - Success state after verification
 * 4. ActivationInProgress - Account activation in progress
 * 5. ActivationFailedFirstTime - Account activation failed first time
 * 6. ProofOfAddressRequired - Additional proof required (optional)
 * 7. ProofOfAddressUpload - Upload proof of address (optional)
 * 8. VerificationFailed - Error state if verification fails (optional)
 * 9. ActivationCompleted - Account activation completed
 */
export function ActivationFlow({
  open,
  onOpenChange,
  currency,
  paymentNetwork,
  vendorId,
  currencies: currenciesProp,
  isPageFlow = false,
}: ActivationFlowProps) {
  const { countryState } = useCountry();
  const { dialogRef, closeDialog, ...dialogFlow } =
    useDialogFlow<ActivationStepType>();
  const { stepFlowRef, ...stepFlow } = useStepFlow<ActivationStepType>();
  const { setStatus, proceed, drawback, createStep } = isPageFlow
    ? stepFlow
    : dialogFlow;

  const onClose = useCallback(() => {
    if (isPageFlow) {
      stepFlow.reset();
    } else {
      closeDialog();
    }
  }, [isPageFlow, stepFlow, dialogFlow]);

  const [formData, setFormData] = useState<{
    ssn?: string;
    streetName?: string;
    buildingName?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
    agreeToTerms?: boolean;
    // CaraForm fields
    annualTransactionVolume?: number;
    annualIncome?: number;
    occupation?: number;
    sourceOfWealth?: string;
    sourceOfFundsCountry?: string;
    transactionCountry?: string;
    citizenshipByInvestment?: string;
  }>({});
  const [uploadedFile, setUploadedFile] = useState<{
    name: string;
    size: number;
    progress?: number;
    fileId?: string;
  }>();
  const [caraType, setCaraType] = useState<CaraTypeEnum>(CaraTypeEnum.All);
  const { getFiatAccounts } = useFiatAccounts();

  // Fetch currencies data (will be used as fallback if not provided from parent)
  // Use same cacheKey as useFiatAccess to share React Query cache
  const { data: currenciesFromQuery, refetch: refetchCurrencies } = useApiQuery(
    fetchGetCurrencies,
    {
      cacheKey: 'fiat-currencies',
    },
  );

  // Use passed-in currencies data or fetch it
  const currencies = currenciesProp || currenciesFromQuery;

  // Calculate initial step immediately when currencies data is available
  const initialStep = useMemo(() => {
    if (!currencies) return undefined;

    const currencyInfo =
      currencies.data[currency as keyof GetCurrenciesResponse['data']];

    return getActivationDefaultStep(countryState, currencyInfo, currency);
  }, [currency, currencies, countryState]);

  const { mutate: activateCurrency, isPending: isSubmitting } = useApiMutation(
    fetchActivateCurrency,
    {
      onSuccess: async (response) => {
        getFiatAccounts();

        // Handle special error code: need to refetch to get detailed status
        if (response.code === 1000034) {
          const { data: latestCurrencies } = await refetchCurrencies();
          const currencyInfo =
            latestCurrencies?.data[
              currency as keyof GetCurrenciesResponse['data']
            ];
          const currencyStatus = currencyInfo?.status;

          if (currencyStatus === CURRENCY_STATUS.POA_AUTO_RETRY) {
            setStatus(ActivationStep.ActivationFailedFirstTime);
          } else if (
            currencyStatus === CURRENCY_STATUS.POA_MANUAL_INITIATED ||
            currencyStatus === CURRENCY_STATUS.POA_MANUAL_RETRY
          ) {
            setStatus(ActivationStep.ProofOfAddressRequired);
          } else {
            const nextStep = getActivationDefaultStep(
              countryState,
              currencyInfo,
              currency,
            );
            setStatus(nextStep);
          }
          return;
        }

        // Handle activation submitted code
        if (response.code === 1000035) {
          setStatus(ActivationStep.InformationSubmitted);
          return;
        }

        // Handle success codes
        if (response.code === 0 || response.code === 1000001) {
          const nextStep = determineNextStep(response.data);
          setStatus(nextStep);
          return;
        }

        // For other cases, determine next step based on response data
        const nextStep = determineNextStep(response.data);
        setStatus(nextStep);
      },
    },
  );

  // Update step when currency changes
  useEffect(() => {
    if (open && initialStep) {
      setStatus(initialStep);
    }
  }, [currency, initialStep, open, setStatus]);

  useEffect(() => {
    const currencyInfo =
      currencies?.data[currency as keyof GetCurrenciesResponse['data']];
    if (
      initialStep === ActivationStep.CaraForm &&
      currencyInfo?.status === CURRENCY_STATUS.EDD_Q1_PENDING
    ) {
      setCaraType(CaraTypeEnum.One);
    }
  }, [initialStep, currency, currencies]);

  useEffect(() => {
    const currencyInfo =
      currencies?.data[currency as keyof GetCurrenciesResponse['data']];

    if (currencyInfo?.poa?.address?.address_line_1) {
      setFormData({
        streetName: currencyInfo.poa.address.address_line_1,
        buildingName: currencyInfo.poa.address.address_line_2 || '',
        city: currencyInfo.poa.address.city || '',
        state: currencyInfo.poa.address.state || '',
        country: currencyInfo.poa.address.country || '',
        zipCode: currencyInfo.poa.address.postcode || '',
        ssn: currencyInfo.poa.address.national_identifier || '',
      });
    }
  }, [currency, currencies]);

  const steps = useMemo(
    () => [
      // Step 0: Cara Form
      createStep({
        status: ActivationStep.CaraForm,
        component: CaraForm,
        componentProps: () => ({
          currency,
          caraType,
          isSubmitting,
          onSubmit: async (data: any) => {
            setFormData(data);

            const params = buildActivateParamsWithEDD(
              currency,
              paymentNetwork,
              vendorId,
              data,
            );

            activateCurrency(params, {
              onSuccess: async () => {
                // Refresh fiat accounts and currencies after successful EDD submission
                getFiatAccounts();
                await refetchCurrencies();
                // Only proceed to next step on success
                proceed(data);
              },
              // onError: will be handled automatically, stays on current step
            });
          },
        }),
      }),
      // Step 1: Address Form
      createStep({
        status: ActivationStep.AddressForm,
        component: AddressForm,
        componentProps: () => ({
          defaultValues: formData,
          onSubmit: (data: any) => {
            setFormData(data);
            proceed(data);
          },
        }),
      }),

      // Step 2: Address Review
      createStep({
        status: ActivationStep.AddressReview,
        component: AddressReview,
        componentProps: () => ({
          isSubmitting,
          ssn: formData.ssn || '',
          address: {
            streetAddress: formData.streetName || '',
            apartment: formData.buildingName,
            city: formData.city || '',
            state: formData.state || '',
            country: formData.country || 'United States',
            zipCode: formData.zipCode || '',
          },
          onEdit: () => {
            drawback();
          },
          onSubmit: async () => {
            setStatus(ActivationStep.ActivationInProgress);

            const params = buildActivateParamsWithPOA(
              currency,
              paymentNetwork,
              vendorId,
              formData,
            );

            activateCurrency(params);
          },
        }),
      }),

      // Step 3: Information Submitted
      createStep({
        status: ActivationStep.InformationSubmitted,
        component: InformationSubmitted,
        ...(isPageFlow ? {} : { onClose }),
        componentProps: () => ({
          onReturnToDeposits: onClose,
        }),
      }),

      // Step 4: Activation In Progress
      createStep({
        status: ActivationStep.ActivationInProgress,
        component: ActivationInProgress,
        componentProps: () => ({
          onReturnToDeposits: onClose,
        }),
      }),

      // Step 5: Activation Failed First Time
      createStep({
        status: ActivationStep.ActivationFailedFirstTime,
        component: VerificationFailedFirstTime,
        componentProps: () => ({
          onMaybeLater: onClose,
          onResubmit: () => {
            setStatus(ActivationStep.AddressReview);
          },
        }),
      }),

      // Step 6: Proof of Address Required
      createStep({
        status: ActivationStep.ProofOfAddressRequired,
        component: ProofOfAddressRequired,
        componentProps: () => ({
          onMaybeLater: onClose,
          onResubmitNow: () => {
            setStatus(ActivationStep.ProofOfAddressUpload);
          },
        }),
      }),

      // Step 7: Proof of Address Upload
      createStep({
        status: ActivationStep.ProofOfAddressUpload,
        component: ProofOfAddressUpload,
        componentProps: () => ({
          isSubmitting,
          uploadedFile,
          onFileSelect: async (file: File) => {
            setUploadedFile({
              name: file.name,
              size: file.size,
              progress: 0,
            });

            try {
              const formData = new FormData();
              formData.append('document_type', 'proof_of_address');
              formData.append('file', file);

              const uploadResult = await fetchUploadDocuments(formData);

              setUploadedFile({
                name: file.name,
                size: file.size,
                fileId: uploadResult.file_id,
              });
            } catch (err) {
              console.error('File upload failed:', err);
              setUploadedFile(undefined);
            }
          },
          onFileRemove: () => {
            setUploadedFile(undefined);
          },
          onMaybeLater: () => {
            closeDialog();
          },
          onSubmit: async () => {
            if (!uploadedFile?.fileId) {
              console.error('No file_id available');
              return;
            }

            setStatus(ActivationStep.ActivationInProgress);

            // Get address from currencyInfo (for second time failure)
            const currencyInfo =
              currencies?.data[currency as keyof GetCurrenciesResponse['data']];
            const existingAddress = currencyInfo?.poa?.address;

            // Merge existing address with form data
            const addressData = {
              streetName:
                existingAddress?.address_line_1 || formData.streetName || '',
              buildingName:
                existingAddress?.address_line_2 || formData.buildingName || '',
              city: existingAddress?.city || formData.city || '',
              state: existingAddress?.state || formData.state || '',
              zipCode: existingAddress?.postcode || formData.zipCode || '',
              country: existingAddress?.country || formData.country || '',
              ssn: existingAddress?.national_identifier || formData.ssn || '',
            };

            const params = buildActivateParamsWithPOA(
              currency,
              paymentNetwork,
              vendorId,
              addressData,
              [
                {
                  file_id: uploadedFile.fileId,
                  type: 'proof_of_address',
                },
              ],
            );

            activateCurrency(params);
          },
          isSubmitDisabled:
            !uploadedFile ||
            uploadedFile.progress !== undefined ||
            isSubmitting,
        }),
      }),

      // Step 8: Verification Failed
      createStep({
        status: ActivationStep.VerificationFailed,
        component: VerificationFailed,
        componentProps: () => ({
          onContactUs: () => {
            closeDialog();
          },
          onGotIt: () => {
            closeDialog();
          },
        }),
      }),

      // step 10: Activation Completed
      createStep({
        status: ActivationStep.ActivationCompleted,
        component: ActivationCompleted,
        componentProps: () => ({
          onReturnToDeposits: () => {
            closeDialog();
          },
        }),
      }),
    ],
    [
      formData,
      currencies,
      uploadedFile,
      currency,
      caraType,
      paymentNetwork,
      vendorId,
      isSubmitting,
      createStep,
      proceed,
      drawback,
      setStatus,
      closeDialog,
      activateCurrency,
      countryState,
    ],
  );

  const handleOpenChange = useCallback(
    (val: boolean) => {
      onOpenChange(val);
      if (open && !val) {
        refetchCurrencies();
      }
    },
    [open, onOpenChange, refetchCurrencies],
  );

  const handleReset = useCallback(() => {
    refetchCurrencies();
  }, [refetchCurrencies]);

  const handleClose = useCallback(() => {
    setFormData({});
    setUploadedFile(undefined);
  }, []);

  return (
    initialStep &&
    (isPageFlow ? (
      <StepFlow
        ref={stepFlowRef}
        steps={steps}
        defaultStatus={initialStep}
        onReset={handleReset}
      />
    ) : (
      <DialogFlow
        ref={dialogRef as Ref<DialogFlowInstance<string>>}
        open={open}
        steps={steps}
        historyMode={true}
        defaultStatus={initialStep}
        onOpenChange={handleOpenChange}
        onClose={handleClose}
      />
    ))
  );
}
